import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Globe, Home as HomeIcon, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { translations } from '../data/mockData';

const Header = () => {
  const { language, setLanguage } = useLanguage();
  const { setIsCartOpen, getItemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState('https://customer-assets.emergentagent.com/job_agency-services-4/artifacts/6niw4y6x_Gemini_Generated_Image_4afo654afo654afo%20copy.png');
  const t = translations[language].nav;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load logo from settings
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/settings/logo`);
        if (response.ok) {
          const data = await response.json();
          if (data.logo_url) {
            setLogoUrl(data.logo_url);
          }
        }
      } catch (error) {
        // Use default logo
      }
    };
    loadLogo();
  }, []);

  const scrollToTop = () => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const scrollToSection = (id) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll to section
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setMobileMenuOpen(false);
  };

  const languages = [
    { code: 'es', label: 'ES' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
  ];

  const homeText = {
    en: 'Home',
    es: 'Inicio',
    de: 'Startseite'
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#141619]/95 backdrop-blur-md border-b border-[#2a3441]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1920px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Link for right-click, onClick for left-click */}
          <div className="flex items-center">
            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
              className="hover:opacity-80 transition-opacity duration-300"
            >
              <img 
                src={logoUrl} 
                alt="PrimeDigital" 
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={scrollToTop}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium flex items-center space-x-1"
            >
              <HomeIcon className="w-4 h-4" />
              <span>{homeText[language]}</span>
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium"
            >
              {t.services}
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium"
            >
              {t.portfolio}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium"
            >
              {t.about}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium"
            >
              {t.testimonials}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 text-base font-medium"
            >
              {t.contact}
            </button>
          </nav>

          {/* Right side - Language, Cart, Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="hidden md:flex items-center space-x-2 border border-[#2a3441] rounded-full px-3 py-1.5 bg-[#1a1d23]/50">
              <Globe className="w-4 h-4 text-[#5A6F8F]" />
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`text-xs font-mono uppercase tracking-wider transition-all duration-300 px-2 py-1 rounded-full ${
                    language === lang.code
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] font-semibold'
                      : 'text-gray-400 hover:text-[#D4AF37]'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 hover:bg-[#1a1d23] rounded-full transition-colors duration-300 group"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6 text-gray-300 group-hover:text-[#D4AF37] transition-colors" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {getItemCount()}
                </span>
              )}
            </button>

            {/* Client Portal Button */}
            <Link
              to="/client/login"
              className="p-2 hover:bg-[#1a1d23] rounded-full transition-colors duration-300 group hidden sm:block"
              data-testid="client-portal-button"
            >
              <User className="w-6 h-6 text-gray-300 group-hover:text-[#D4AF37] transition-colors" />
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-[#1a1d23] rounded-full transition-colors duration-300"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#141619] border-t border-[#2a3441] shadow-2xl">
          <div className="px-4 py-6 space-y-4">
            <button
              onClick={scrollToTop}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base flex items-center space-x-2"
            >
              <HomeIcon className="w-4 h-4" />
              <span>{homeText[language]}</span>
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base"
            >
              {t.services}
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base"
            >
              {t.portfolio}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base"
            >
              {t.about}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base"
            >
              {t.testimonials}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2 text-base"
            >
              {t.contact}
            </button>

            {/* Mobile Language Selector */}
            <div className="pt-4 border-t border-[#2a3441]">
              <div className="flex items-center space-x-2 mb-3">
                <Globe className="w-4 h-4 text-[#5A6F8F]" />
                <span className="text-sm text-gray-400">
                  {language === 'es' ? 'Idioma:' : language === 'en' ? 'Language:' : 'Sprache:'}
                </span>
              </div>
              <div className="flex space-x-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`text-xs font-mono uppercase tracking-wider transition-all duration-300 px-3 py-2 rounded-full ${
                      language === lang.code
                        ? 'bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] font-semibold'
                        : 'bg-[#1a1d23] text-gray-400 hover:text-[#D4AF37]'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Mobile Client Portal Link */}
            <div className="pt-4 border-t border-[#2a3441]">
              <Link
                to="/client/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-2 text-gray-300 hover:text-[#D4AF37] transition-colors duration-300 py-2"
              >
                <User className="w-5 h-5" />
                <span>{language === 'es' ? 'Mi Cuenta' : language === 'en' ? 'My Account' : 'Mein Konto'}</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
